import React, { type ReactNode, useRef } from 'react';
import {
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type TextInputFieldProps } from './text-input-field-props';

export type TextInputProps = {
  children: ReactNode;
};

const TextInput = ({ children }: TextInputProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<TextInputFieldProps>();

  const labelElement = findChildOfType(children, TextInput.Label);
  const inputElement = findChildOfType(children, TextInput.Input);
  const descriptionElement = findChildOfType(children, TextInput.Description);
  const errorElement = findChildOfType(children, TextInput.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <TextFieldRAC
          value={value ?? ''}
          onChange={onChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {inputElement}
        </TextFieldRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type InputProps = {
  className?: string;
};

const Input = ({ className }: InputProps) => {
  const { id, placeholder } = useFieldPropsV2<TextInputFieldProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();

  const inputRef = useRef<HTMLInputElement>(null);

  useFocusFieldEvent(() => {
    inputRef.current?.focus();
  }, id);

  return (
    <InputRAC
      ref={inputRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      placeholder={placeholder}
    />
  );
};

TextInput.Label = Label;
TextInput.Input = Input;
TextInput.Description = Description;
TextInput.Error = Error;

export { TextInput };
